import {
  HttpClient,
  HttpResponse,
} from '@angular/common/http';
import {
  Component,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { saveAs } from 'file-saver';
import { DateTime } from 'luxon';
import {
  BsModalRef,
  BsModalService,
} from 'ngx-bootstrap/modal';
import {
  FileSystemDirectoryEntry,
  FileSystemEntry,
  FileSystemFileEntry,
  NgxFileDropEntry,
} from 'ngx-file-drop';
import { NGXLogger } from 'ngx-logger';
import prettyBytes from 'pretty-bytes';
import {
  firstValueFrom,
  Observable,
  Subscription,
  timer,
} from 'rxjs';
import {
  CancelBookingDialogComponent,
  ChangeBookingDialogComponent,
  ConfirmationDialogComponent,
  DoPaymentDialogComponent,
} from 'src/app/components';
import Tools from 'src/app/lib/Tools';
import {
  BookingService,
  ExamEventService,
  NotificationService,
  ProfileService,
} from 'src/app/services';
import {
  AdditionalBookingQuestionsResponse,
  EventBooking,
  EventBookingFileInfo,
  EventBookingHistoryEvents,
  ExamEvent,
  InfotextResponse,
  LineBreakBlock,
  LinkResponseBlock,
  ListResponse,
  ResponseBlock,
  SelectOption,
  TextFormattingType,
  TextResponseBlock,
  University,
} from 'src/app/types';
import { ExamBookingAddQuestionsUserAnswers } from 'src/app/types/ExamBookingAddQuestionsUserAnswers';
import { environment } from 'src/environments/environment';
import { BreadcrumbService } from 'xng-breadcrumb';
import { TenantId } from '../../../environments/environments.types';
import { ChangePaymentStatusDialogComponent } from '../../components';

interface AdminEditSection<TValue> {
  active: boolean;
  loadingOptions: boolean;
  optionsLoaded: boolean;
  saving: boolean;
  options: SelectOption[];
  control: FormControl<TValue | null>;
}

const APPLICATION_UNIVERSITIES_TITLE_KEYS: Record<
  string,
  string
> = {
  PhaST: 'phast.question.university.text',
  'TM-WISO': 'tm-wiso.questions.apply-for-university.text',
  'ITB-Business':
    'itb-business.questions.apply-for-university.text',
};

const AUTO_SEND_UNIVERSITIES_TITLE_KEYS: Record<
  string,
  string
> = {
  'TM-WISO': 'tm-wiso.questions.send-results.text',
  'ITB-Business':
    'itb-business.questions.send-results.text',
};

@Component({
  selector: 'app-exam-booking-summary-page',
  templateUrl: './exam-booking-summary-page.component.html',
  styleUrls: ['./exam-booking-summary-page.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ExamBookingSummaryPageComponent
  implements OnInit, OnDestroy
{
  booking!: EventBooking;
  bookingFiles!: EventBookingFileInfo[];
  bookingLoadedCounter = 0;
  bookingPaymentExpectedUntil: string | undefined =
    undefined;
  examEvent!: ExamEvent;
  isHoveringOverUpload!: boolean;
  isLoading!: boolean;
  latestBookingHistory!: EventBookingHistoryEvents;
  modalRef?: BsModalRef;
  userAnswers: ExamBookingAddQuestionsUserAnswers = {
    variant: '',
    language: '',
    universityIds: [],
    sendResultsToUniversityIds: [],
    finalGrade: '',
  };
  selectedPaymentMethod = '';

  adminEdit: {
    language: AdminEditSection<string>;
    applicationUniversities: AdminEditSection<string[]>;
    autoSendUniversities: AdminEditSection<string[]>;
  } = {
    language: {
      active: false,
      loadingOptions: false,
      optionsLoaded: false,
      saving: false,
      options: [] as SelectOption[],
      control: new FormControl<string | null>(null),
    },
    applicationUniversities: {
      active: false,
      loadingOptions: false,
      optionsLoaded: false,
      saving: false,
      options: [] as SelectOption[],
      control: new FormControl<string[] | null>(null),
    },
    autoSendUniversities: {
      active: false,
      loadingOptions: false,
      optionsLoaded: false,
      saving: false,
      options: [] as SelectOption[],
      control: new FormControl<string[] | null>(null),
    },
  };

  private addQuestions!: AdditionalBookingQuestionsResponse;
  private availableApplicationUniversities: University[] =
    [];
  private availableAutoSendUniversities: University[] = [];
  private bookingId!: number;
  private bookingLoadSequence = 0;
  private currentlyUploading: { [key: string]: boolean } =
    {};
  private everyTenSeconds: Observable<number> = timer(
    10000,
    10000
  );
  private infotext?: InfotextResponse;
  private unsubOnDestroy = new Subscription();

  constructor(
    private activatedRoute: ActivatedRoute,
    private bookingService: BookingService,
    private breadcrumbService: BreadcrumbService,
    private examEventService: ExamEventService,
    private http: HttpClient,
    private logger: NGXLogger,
    private modalService: BsModalService,
    private notificationService: NotificationService,
    private profileService: ProfileService,
    private translateService: TranslateService
  ) {
    this.breadcrumbService.set('@bookingName', {
      skip: true,
    });
  }

  private withAdminEdit<TValue>(
    section: AdminEditSection<TValue>
  ): {
    start: (
      loadOptions: () => Promise<void>,
      initValue: () => TValue | null
    ) => Promise<void>;
    cancel: () => void;
    save: (
      saveValue: (value: TValue) => Promise<void>
    ) => Promise<void>;
  } {
    return {
      start: async (loadOptions, initValue) => {
        if (!this.isAdmin) {
          return;
        }

        section.active = true;
        if (section.optionsLoaded) {
          section.control.setValue(initValue());
          return;
        }

        // Options not loaded yet: load first, then initialize the control value
        section.loadingOptions = true;
        try {
          await loadOptions();
          section.control.setValue(initValue());
        } catch (e) {
          this.logger.error(e);
          await this.notificationService.error(
            'error.general'
          );
        } finally {
          section.loadingOptions = false;
        }
      },
      cancel: () => {
        section.active = false;
        section.saving = false;
        section.control.setValue(null);
      },
      save: async (saveValue) => {
        if (!this.isAdmin) {
          return;
        }
        const value = section.control.value;
        if (value === null || value === undefined) {
          return;
        }

        section.saving = true;
        try {
          await saveValue(value as TValue);
          await this.reloadBookingAndQuestions();
          section.active = false;
        } catch (e) {
          this.logger.error(e);
          await this.notificationService.error(
            'error.general'
          );
        } finally {
          section.saving = false;
        }
      },
    };
  }

  async ngOnInit(): Promise<void> {
    this.isLoading = true;

    const bookingIdParam =
      this.activatedRoute.snapshot.paramMap.get('id');

    if (bookingIdParam) {
      this.bookingId = Number(
        bookingIdParam.match(/^\d+/i)
      );
      try {
        await this.profileService.load();
        await this.getBooking();

        this.unsubOnDestroy.add(
          this.getTranslation('title.booking').subscribe(
            (translation) => {
              this.breadcrumbService.set('@bookingName', {
                label: `${translation} #${this.booking.bookingReference}`,
                skip: false,
              });
            }
          )
        );

        await this.getExamEvent(this.booking.testEventId);

        this.addQuestions =
          await this.bookingService.loadAdditionalBookingQuestions(
            this.booking.bookingId,
            { loadDeletedBookings: true }
          );

        if (this.isAdmin) {
          await this.loadAdminUniversityOptions();
        }

        this.applyUniversityTranslations([
          ...(this.addQuestions.universities || []),
          ...(this.addQuestions.sendResultsToUniversities ||
            []),
        ]);

        this.getUserAnswers(this.booking);
        this.isLoading = false;

        this.unsubOnDestroy.add(
          this.everyTenSeconds.subscribe(() => {
            if (!this.isAdminEditSaving) {
              this.getBooking();
            }
          })
        );
        this.unsubOnDestroy.add(
          this.translateService.onLangChange.subscribe(
            () => {
              this.retrieveInfotext();
            }
          )
        );
      } catch (e: unknown) {
        this.fail(e);
      }

      await this.getBookingFiles();
    }
  }

  get tenant(): TenantId {
    return environment.tenant;
  }

  get currentLanguage(): string {
    return this.translateService.currentLang;
  }

  get timeSlot(): Date | undefined {
    if (this.examEvent) {
      return Tools.dateFromIso(
        this.examEvent.testeventTime
      );
    }
    return;
  }

  get timeSlotDate(): string | undefined {
    if (this.examEvent?.testeventTime) {
      return (
        DateTime.fromISO(
          this.examEvent.testeventTime
        ).toISODate() ?? undefined
      );
    }
    return undefined;
  }

  get timeSlotTime(): string | undefined {
    if (this.examEvent?.testeventTime) {
      return (
        DateTime.fromISO(
          this.examEvent.testeventTime
        ).toISOTime() ?? undefined
      );
    }
    return undefined;
  }

  get isAdmin(): boolean {
    return this.profileService.isAdmin;
  }

  private get isAdminEditSaving(): boolean {
    return Object.values(this.adminEdit).some(
      ({ saving }) => saving
    );
  }

  get applicationUniversitiesTitleKey(): string {
    return (
      APPLICATION_UNIVERSITIES_TITLE_KEYS[
        this.booking?.test.templateName
      ] ??
      'booking.default.summary.application-universities'
    );
  }

  get autoSendUniversitiesTitleKey(): string {
    return (
      AUTO_SEND_UNIVERSITIES_TITLE_KEYS[
        this.booking?.test.templateName
      ] ?? 'booking.default.summary.auto-send-universities'
    );
  }

  get showApplicationUniversities(): boolean {
    if (this.isAdmin) {
      return (
        this.adminEdit.applicationUniversities
          .optionsLoaded &&
        this.adminEdit.applicationUniversities.options
          .length > 0
      );
    }

    return (
      (this.addQuestions?.universities?.length ?? 0) > 0
    );
  }

  get showAutoSendUniversities(): boolean {
    if (this.isAdmin) {
      return (
        this.adminEdit.autoSendUniversities.optionsLoaded &&
        this.availableAutoSendUniversities.length > 0
      );
    }

    return (
      (this.addQuestions?.sendResultsToUniversities
        ?.length ?? 0) > 0
    );
  }

  get paymentCouldArriveShortly(): boolean {
    if (
      this.booking.status !== 'open' &&
      this.booking.status !== 'pending'
    ) {
      return false;
    }
    if (this.booking.deleted) {
      return false;
    }
    return this.bookingLoadedCounter <= 2;
  }

  get canBePaidFor(): boolean {
    if (this.booking.deleted) {
      return false;
    }
    if (this.paymentCouldArriveShortly) {
      return false;
    }
    return [
      'open',
      'pending',
      'failed',
      'canceled',
    ].includes(this.booking.status);
  }

  /**
   * Retrieve the automatic cancelation timestamp.
   * If no payment has been started, there will be no "paymentExpectedUntil" field.
   */
  get automaticCancelDate(): string | undefined {
    const { reservedUntil, paymentExpectedUntil } =
      this.booking;
    if (!paymentExpectedUntil) {
      return reservedUntil;
    } else {
      return reservedUntil < paymentExpectedUntil
        ? reservedUntil
        : paymentExpectedUntil;
    }
  }

  get paymentIsComplete(): boolean {
    if (!this.booking) {
      return false;
    }
    return ['paid', 'not_required'].includes(
      this.booking.status
    );
  }

  private getFormattingFromBlock(
    block: ResponseBlock
  ): TextFormattingType[] {
    switch (block.type) {
      case 'HEADING': {
        return [];
      }
      case 'TEXT': {
        return block.formatting || [];
      }
      case 'LINK': {
        return block.formatting || [];
      }
      case 'LINE_BREAK': {
        return [];
      }
    }
  }

  isBoldInfotext(block: ResponseBlock): boolean {
    return this.getFormattingFromBlock(block).includes(
      'BOLD'
    );
  }

  isItalicInfotext(block: ResponseBlock): boolean {
    return this.getFormattingFromBlock(block).includes(
      'ITALIC'
    );
  }

  isUnderlinedInfotext(block: ResponseBlock): boolean {
    return this.getFormattingFromBlock(block).includes(
      'UNDERLINED'
    );
  }

  getLinkFromLinkBlock(block: ResponseBlock): string {
    return (block as LinkResponseBlock).link;
  }

  doPayment(): void {
    this.modalService.show(DoPaymentDialogComponent, {
      class: 'modal-md',
      initialState: {
        bookingId: this.booking.bookingId,
      },
    });
  }

  changePaymentStatusDialog(): void {
    const modalRef = this.modalService.show(
      ChangePaymentStatusDialogComponent,
      {
        class: 'modal-lg',
        initialState: {
          data: {
            bookingId: this.booking.bookingId,
            bookingReference: this.booking.bookingReference,
            currentPaymentStatus: this.booking.status,
          },
        },
      }
    );

    modalRef.onHidden?.subscribe(() => {
      const result = modalRef.content?.result;
      if (result?.changed) {
        this.getBooking();
      }
    });
  }

  changeBookingDialog(): void {
    this.modalRef = this.modalService.show(
      ChangeBookingDialogComponent,
      {
        class: 'modal-lg',
        initialState: { bookingId: this.bookingId },
      }
    );
  }

  cancelBookingDialog(): void {
    this.modalRef = this.modalService.show(
      CancelBookingDialogComponent,
      {
        class: 'modal-lg',
        initialState: { bookingId: this.bookingId },
      }
    );
  }

  ngOnDestroy(): void {
    this.unsubOnDestroy.unsubscribe();
  }

  private getTranslation(key: string): Observable<string> {
    return this.translateService.stream(key);
  }

  private async getExamEvent(
    testEventId: number
  ): Promise<void> {
    this.examEvent =
      await this.examEventService.loadByEventId(
        testEventId
      );
  }

  private async getBooking(): Promise<void> {
    const loadSequence = ++this.bookingLoadSequence;
    const booking =
      await this.bookingService.loadBookingById(
        this.bookingId,
        {
          loadDeletedBookings: true,
        }
      );

    if (loadSequence !== this.bookingLoadSequence) {
      return;
    }

    this.booking = booking;
    this.getExamEvent(this.booking.testEventId);
    this.bookingLoadedCounter++;
    if (!this.infotext) {
      await this.retrieveInfotext();
    }

    this.loadBookingHistory();
    this.bookingPaymentExpectedUntil =
      this.booking?.paymentExpectedUntil;
  }

  async startAdminEditLanguage(): Promise<void> {
    return this.withAdminEdit(
      this.adminEdit.language
    ).start(
      async () => {
        const languages =
          await this.bookingService.loadAvailableLanguagesByBookingIdAdmin(
            this.booking.bookingId
          );
        this.adminEdit.language.options = languages.map(
          (lang) => ({ value: lang, label: lang })
        );
        this.adminEdit.language.optionsLoaded = true;
      },
      () => this.userAnswers.language ?? null
    );
  }

  cancelAdminEditLanguage(): void {
    this.withAdminEdit(this.adminEdit.language).cancel();
  }

  async saveAdminEditLanguage(): Promise<void> {
    return this.withAdminEdit(this.adminEdit.language).save(
      async (language) => {
        await this.bookingService.setLanguageByBookingIdAdmin(
          this.booking.bookingId,
          language
        );
      }
    );
  }

  async startAdminEditApplicationUniversities(): Promise<void> {
    return this.withAdminEdit(
      this.adminEdit.applicationUniversities
    ).start(
      () => this.loadApplicationUniversityOptions(),
      () => {
        const selected =
          this.booking.additionalQuestionData
            ?.universityIds ?? [];
        return this.mapUniversityIdsToTranslationKeys(
          selected,
          this.availableApplicationUniversities,
          this.adminEdit.applicationUniversities.options
        );
      }
    );
  }

  cancelAdminEditApplicationUniversities(): void {
    this.withAdminEdit(
      this.adminEdit.applicationUniversities
    ).cancel();
  }

  async saveAdminEditApplicationUniversities(): Promise<void> {
    return this.withAdminEdit(
      this.adminEdit.applicationUniversities
    ).save(async (translationKeys) => {
      await this.bookingService.setApplicationUniversitiesByBookingIdAdmin(
        this.booking.bookingId,
        translationKeys
      );
    });
  }

  async startAdminEditAutoSendUniversities(): Promise<void> {
    return this.withAdminEdit(
      this.adminEdit.autoSendUniversities
    ).start(
      () => this.loadAutoSendUniversityOptions(),
      () => {
        const selected =
          this.booking.additionalQuestionData
            ?.sendResultsToUniversityIds ?? [];
        return this.mapUniversityIdsToTranslationKeys(
          selected,
          this.availableAutoSendUniversities,
          this.adminEdit.autoSendUniversities.options
        );
      }
    );
  }

  cancelAdminEditAutoSendUniversities(): void {
    this.withAdminEdit(
      this.adminEdit.autoSendUniversities
    ).cancel();
  }

  async saveAdminEditAutoSendUniversities(): Promise<void> {
    return this.withAdminEdit(
      this.adminEdit.autoSendUniversities
    ).save(async (translationKeys) => {
      await this.bookingService.setAutoSendUniversitiesByBookingIdAdmin(
        this.booking.bookingId,
        translationKeys
      );
    });
  }

  private applyUniversityTranslations(
    universities: University[]
  ): void {
    universities.forEach(
      ({ translationKey, translations }) => {
        for (let language in translations) {
          this.translateService.setTranslation(
            language,
            { [translationKey]: translations[language] },
            true
          );
        }
      }
    );
  }

  private async loadAdminUniversityOptions(): Promise<void> {
    try {
      await Promise.all([
        this.loadApplicationUniversityOptions(),
        this.loadAutoSendUniversityOptions(),
      ]);
    } catch (e) {
      this.logger.error(e);
      await this.notificationService.error('error.general');
    }
  }

  private async loadApplicationUniversityOptions(): Promise<void> {
    const universities =
      await this.bookingService.loadAvailableApplicationUniversitiesByBookingIdAdmin(
        this.booking.bookingId
      );
    this.availableApplicationUniversities = universities;
    this.setUniversityOptions(
      this.adminEdit.applicationUniversities,
      universities
    );
  }

  private async loadAutoSendUniversityOptions(): Promise<void> {
    const universities =
      await this.bookingService.loadAvailableAutoSendUniversitiesByBookingIdAdmin(
        this.booking.bookingId
      );
    this.availableAutoSendUniversities = universities;
    this.setAutoSendUniversityOptions();
  }

  private setAutoSendUniversityOptions(): void {
    const applicationUniversityIds = new Set(
      this.booking.additionalQuestionData?.universityIds ??
        []
    );
    this.setUniversityOptions(
      this.adminEdit.autoSendUniversities,
      this.availableAutoSendUniversities.filter(({ id }) =>
        applicationUniversityIds.has(id)
      )
    );
  }

  private setUniversityOptions(
    section: AdminEditSection<string[]>,
    universities: University[]
  ): void {
    this.applyUniversityTranslations(universities);
    section.options = universities.map(
      ({ translationKey }) => ({
        value: translationKey,
        label: translationKey,
      })
    );
    section.optionsLoaded = true;
  }

  private mapUniversityIdsToTranslationKeys(
    selectedIds: number[],
    availableUniversities: University[] | undefined,
    options: SelectOption[]
  ): string[] {
    const translationKeyById = new Map<number, string>();

    (availableUniversities || []).forEach(
      ({ id, translationKey }) => {
        translationKeyById.set(id, translationKey);
      }
    );

    [
      ...(this.addQuestions?.universities || []),
      ...(this.addQuestions?.sendResultsToUniversities ||
        []),
    ].forEach(({ id, translationKey }) => {
      if (!translationKeyById.has(id)) {
        translationKeyById.set(id, translationKey);
      }
    });

    return selectedIds
      .map((id) => translationKeyById.get(id))
      .filter((key): key is string => !!key)
      .filter((key) =>
        options.some((o) => o.value === key)
      );
  }

  private async reloadBookingAndQuestions(): Promise<void> {
    await this.getBooking();
    this.addQuestions =
      await this.bookingService.loadAdditionalBookingQuestions(
        this.booking.bookingId,
        { loadDeletedBookings: true }
      );

    this.applyUniversityTranslations([
      ...(this.addQuestions.universities || []),
      ...(this.addQuestions.sendResultsToUniversities ||
        []),
    ]);

    if (this.isAdmin) {
      this.setAutoSendUniversityOptions();
    }

    this.getUserAnswers(this.booking);
  }

  private async retrieveInfotext() {
    if (this.paymentIsComplete) {
      try {
        this.infotext =
          await this.bookingService.getInfotextForCompletedBooking(
            this.bookingId,
            this.currentLanguage
          );
      } catch (e) {
        console.log(
          `No infotext for completed booking ${this.bookingId}.`
        );
        this.infotext = {};
      }
    }
  }

  private getUserAnswers(booking: EventBooking): void {
    this.userAnswers.universityIds =
      this.addQuestions.universities?.filter((uni) =>
        booking.additionalQuestionData?.universityIds?.includes(
          uni.id
        )
      );
    this.userAnswers.sendResultsToUniversityIds =
      this.addQuestions.sendResultsToUniversities?.filter(
        (uni) =>
          booking.additionalQuestionData?.sendResultsToUniversityIds?.includes(
            uni.id
          )
      );

    switch (booking.test.templateName) {
      case 'TM-WISO': {
        this.userAnswers.language =
          booking.additionalQuestionData?.testDetails
            ?.language ?? '';

        break;
      }
      case 'PhaST': {
        this.userAnswers.finalGrade =
          booking.additionalQuestionData?.finalGrade;
        break;
      }
      case 'GSAT': {
        this.userAnswers.language =
          booking.additionalQuestionData?.testDetails
            ?.language ?? '';
        break;
      }
      case 'ITB-Business': {
        this.userAnswers.variant =
          booking.additionalQuestionData?.testDetails?.itbBusinessVariant;
        this.userAnswers.language =
          booking.additionalQuestionData?.testDetails
            ?.language ?? '';
        this.userAnswers.finalGrade =
          booking.additionalQuestionData?.finalGrade;
        break;
      }
      case 'ITB-Science': {
        this.userAnswers.language =
          booking.additionalQuestionData?.testDetails
            ?.language ?? '';
        this.userAnswers.finalGrade =
          booking.additionalQuestionData?.finalGrade;
        break;
      }
      case 'ITB-Technology': {
        this.userAnswers.language =
          booking.additionalQuestionData?.testDetails
            ?.language ?? '';
        this.userAnswers.sendResultsToUniversityIds =
          this.addQuestions.sendResultsToUniversities?.filter(
            (uni) =>
              booking.additionalQuestionData?.sendResultsToUniversityIds?.includes(
                uni.id
              )
          );
        this.userAnswers.finalGrade =
          booking.additionalQuestionData?.finalGrade;
        break;
      }
    }
  }

  private async getBookingFiles(): Promise<void> {
    const response =
      await this.bookingService.loadBookingFilesByBookingId(
        this.bookingId
      );
    this.bookingFiles = response.items;
    this.sortBookings();
  }

  private sortBookings(): void {
    this.bookingFiles.sort((a, b) =>
      (a.uploadedAt || '') < (b.uploadedAt || '') ? 1 : -1
    );
  }

  fileSize(fileInfo: EventBookingFileInfo): string {
    return prettyBytes(fileInfo.fileSizeInBytes, {
      locale: this.currentLanguage.substr(0, 2),
    });
  }

  async downloadFile(
    internalFileId: string,
    defaultFileName: string
  ): Promise<void> {
    try {
      const response =
        await this.bookingService.getBookingFileByFileKey(
          this.bookingId,
          internalFileId
        );

      if (!response.body) {
        throw new Error(
          `No body present in file download for file key ${internalFileId} in booking ${this.bookingId}.`
        );
      }

      const fileName =
        this.fileNameFromContentDispositionHeader(
          response
        ) || defaultFileName;

      saveAs(response.body, fileName);
    } catch (error) {
      this.logger.error(error);
      await this.notificationService.error(
        'error.file-download-failed'
      );
    }
  }

  deleteFile(fileInfo: EventBookingFileInfo): void {
    const modalRef = this.modalService.show(
      ConfirmationDialogComponent,
      {
        class: 'modal-sm',
        initialState: {
          data: {
            title: 'file-download.confirm-deletion.title',
            content:
              'file-download.confirm-deletion.content',
            data: {
              fileName: fileInfo.fileName,
            },
          },
        },
      }
    );

    modalRef.onHidden?.subscribe(async () => {
      const result = modalRef.content?.result;
      if (result === true) {
        try {
          const response =
            await this.bookingService.deleteFileForBooking(
              this.bookingId,
              fileInfo.internalFileId
            );

          if (response.status === 200) {
            // File deleted
            await this.getBookingFiles();
          }
        } catch (error) {
          this.logger.error(error);
          await this.notificationService.error(
            'error.file-deletion-failed'
          );
        }
      }
    });
  }

  private fileNameFromContentDispositionHeader(
    response: HttpResponse<any>
  ): string | undefined {
    const contentDispositionHeader: string =
      response.headers.get('content-disposition') || '';
    const matches = /filename="(?<filename>.*)"/.exec(
      contentDispositionHeader
    );

    return matches?.groups?.filename;
  }

  setHoveringOverUpload(hovering: boolean): void {
    this.isHoveringOverUpload = hovering;
  }

  async uploadFile(
    droppedFiles: NgxFileDropEntry[]
  ): Promise<void> {
    this.setHoveringOverUpload(false);

    await Promise.all(
      droppedFiles.map(({ fileEntry, relativePath }) =>
        this.uploadFileEntries(fileEntry, relativePath)
      )
    );
  }

  private async uploadFileEntries(
    fileSystemEntry: FileSystemEntry,
    relativePath: string
  ): Promise<void> {
    if (fileSystemEntry.isFile) {
      const fileEntry =
        fileSystemEntry as FileSystemFileEntry;

      await fileEntry.file(async (file: File) => {
        // Skip files with size 0 or files that are hidden in unixoid OSes.
        // We can't skip hidden files on Windows, since we don't seem to be able to identify those as hidden.
        if (file.size === 0 || file.name.startsWith('.')) {
          return;
        }

        // Mark that this file is currently being uploaded
        this.currentlyUploading[file.name] = true;

        const uploadedFileInfo =
          await this.bookingService.uploadFileForBooking(
            file,
            relativePath,
            this.bookingId
          );
        this.bookingFiles.push(uploadedFileInfo);
        this.sortBookings();

        // Mark that the upload of this file has been completed
        this.currentlyUploading[file.name] = false;
      });
    } else if (fileSystemEntry.isDirectory) {
      const dirEntry =
        fileSystemEntry as FileSystemDirectoryEntry;
      const dirReader = dirEntry.createReader();
      // For directories, we go through
      dirReader.readEntries((dirFiles: FileSystemEntry[]) =>
        dirFiles.forEach((dirFile) =>
          this.uploadFileEntries(
            dirFile,
            relativePath + '/' + dirEntry.name
          )
        )
      );
    } else {
      // I don't think we can actually trigger this case. In theory (at least on unixoid OSes), this would mean that the
      // file someone tried to upload is:
      // - a block file
      // - a character file
      // - a pipe file
      // - a symbolic link file
      // - a socket file
      // However, the upload mechanism doesn't seem to react to such files at all.
      this.notificationService.error(
        `Could not upload file ${fileSystemEntry.name} because it has an unexpected type (not a file or a directory).`,
        'Error during file upload'
      );
      this.logger.error(
        `Could not upload file ${fileSystemEntry.name} because it has an unexpected type (not a file and not a directory).`
      );
    }
  }

  get isUploading(): boolean {
    const keys = Object.keys(this.currentlyUploading);
    for (const key of keys) {
      if (this.currentlyUploading[key]) {
        return true;
      }
    }
    return false;
  }

  get isOptionalQuestionAnswered(): boolean | undefined {
    const addQuestions =
      this.booking?.additionalQuestionData;

    if (addQuestions) {
      const { finalGrade, studentDetails } = addQuestions;
      const existingAnswers = new Set([
        finalGrade,
        studentDetails?.schoolSubjects?.german?.grade,
        studentDetails?.schoolSubjects?.german?.points,
        studentDetails?.schoolSubjects?.mathematics?.grade,
        studentDetails?.schoolSubjects?.mathematics?.points,
        studentDetails?.germanLanguageLevel,
        studentDetails?.migrationBackground,
      ]);
      // Any of the answers may be undefined, so we'll remove that as it is really a non-answer.
      existingAnswers.delete(undefined);
      return existingAnswers.size > 0; // If at least one answer has been given, return true.
    } else {
      return;
    }
  }

  get infotextBlocks(): ResponseBlock[] {
    if (!this.infotext || !this.infotext.blocks) {
      return [];
    }
    return this.infotext.blocks.flatMap(
      (block: ResponseBlock) => {
        const lines = block.value.split('\n');
        const newBlocks = [];
        for (let i = 0; i < lines.length; i++) {
          newBlocks.push({
            ...block,
            value: lines[i].trim(),
          } as TextResponseBlock);
          // Add line breaks between lines, except for after the last one.
          if (i < lines.length - 1) {
            newBlocks.push({
              type: 'LINE_BREAK',
              value: '',
            } as LineBreakBlock);
          }
        }
        return newBlocks;
      }
    );
  }

  blockStartsWithDot(block: ResponseBlock): boolean {
    return block.value.trimStart().startsWith('.');
  }

  private async loadBookingHistory(): Promise<void> {
    const bookingHistoryEvents = await firstValueFrom(
      this.http.get<
        ListResponse<EventBookingHistoryEvents>
      >(`/api/bookings/booking/${this.bookingId}/history`)
    );

    if (bookingHistoryEvents.total) {
      this.latestBookingHistory =
        bookingHistoryEvents.items[
          bookingHistoryEvents.total - 1
        ];
      this.selectedPaymentMethod =
        this.latestBookingHistory.selectedPaymentMethod ||
        '';
    }
  }

  private fail(e?: unknown): void {
    this.logger.error('load booking details failed', e);
  }
}
